import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@/api/client';
import { useAdminLocationGroups, useCreateAdminLocationGroup, useDeleteAdminLocationGroup, useUpdateAdminLocationGroup, } from '@/api/opengater/useLocations';
import { Input } from '@/components/primitives/Input';
import { PanelForm, SaveBar, SettingsDanger, SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import { createdEntityId } from './creationResult';
import { locationAdminError, locationFieldErrors, requiredTextError } from './locationRules';
import { LocationGroupTranslationEditor } from './TranslationEditor';
export function LocationGroupForm({ groupId, creating = false, onDirtyChange, onCreated, onDone, }) {
    const groups = useAdminLocationGroups();
    const group = groups.data?.find((item) => item.id === groupId) ?? null;
    const create = useCreateAdminLocationGroup();
    const update = useUpdateAdminLocationGroup();
    const remove = useDeleteAdminLocationGroup();
    const [baseName, setBaseName] = useState('');
    const [name, setName] = useState('');
    const [fieldError, setFieldError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [translationDirty, setTranslationDirty] = useState(false);
    const dirtyRef = useRef(false);
    useEffect(() => {
        if (creating || !group || dirtyRef.current)
            return;
        setBaseName(group.name);
        setName(group.name);
    }, [creating, group]);
    useEffect(() => {
        if (!creating && groups.data && !group && !dirtyRef.current)
            onDone();
    }, [creating, group, groups.data, onDone]);
    const dirty = creating ? name !== '' : name.trim() !== baseName;
    const nameError = requiredTextError(name) ?? fieldError;
    const saving = create.isPending || update.isPending;
    const currentError = create.error ?? update.error;
    useEffect(() => {
        dirtyRef.current = dirty || translationDirty;
        onDirtyChange(dirty || translationDirty);
    }, [dirty, onDirtyChange, translationDirty]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
    if (!creating && groups.isLoading)
        return _jsx(SettingsLoading, {});
    if (!creating && ((groups.error && !groups.data) || !group)) {
        return (_jsx(SettingsNote, { tone: "error", children: locationAdminError(groups.error, 'Группа не найдена.') }));
    }
    const submit = async (event) => {
        event.preventDefault();
        if (nameError || saving || (!creating && !dirty))
            return;
        try {
            if (creating) {
                const beforeIds = (groups.data ?? []).map((item) => item.id);
                const response = await create.mutateAsync({ name: name.trim() });
                let id = createdEntityId(response, beforeIds, []);
                try {
                    const refreshed = await groups.refetch({ throwOnError: true });
                    id = createdEntityId(response, beforeIds, (refreshed.data ?? []).map((item) => item.id));
                }
                catch {
                    // The POST succeeded. Catalog refresh can recover independently.
                }
                onCreated(id);
            }
            else if (group) {
                await update.mutateAsync({ id: group.id, patch: { name: name.trim() } });
                const refreshed = await groups.refetch({ throwOnError: true });
                const authoritative = refreshed.data?.find((item) => item.id === group.id);
                if (!authoritative)
                    throw new Error('Saved group was not returned');
                setBaseName(authoritative.name);
                setName(authoritative.name);
            }
            setFieldError(null);
            setSaveError(null);
        }
        catch (error) {
            if (!creating && error instanceof ApiError && error.status === 404) {
                try {
                    await groups.refetch({ throwOnError: true });
                }
                catch {
                    // The stale editor must close even if refreshing the directory also fails.
                }
                onDone();
                return;
            }
            const fields = locationFieldErrors(error, ['name']);
            setFieldError(fields.name ?? null);
            setSaveError(fields.name ? null : locationAdminError(error, 'Не удалось сохранить группу.'));
        }
    };
    return (_jsxs(PanelForm, { meta: creating ? 'Новая группа' : `Группа #${group.id}`, onSubmit: submit, children: [_jsx(SettingsGroup, { title: creating ? 'Новая группа' : 'Группа', children: _jsx(SettingsRow, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", wide: true, error: nameError, children: _jsx(Input, { "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0433\u0440\u0443\u043F\u043F\u044B", "aria-invalid": Boolean(nameError), value: name, disabled: saving, onChange: (event) => {
                            setName(event.target.value);
                            setFieldError(null);
                            setSaveError(null);
                            create.reset();
                            update.reset();
                        } }) }) }), !creating && (_jsx(LocationGroupTranslationEditor, { id: group.id, onDirtyChange: setTranslationDirty })), !creating && (_jsx(SettingsDanger, { label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0433\u0440\u0443\u043F\u043F\u0443", confirmText: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0433\u0440\u0443\u043F\u043F\u0443? \u041B\u043E\u043A\u0430\u0446\u0438\u0438 \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438 \u043D\u0435 \u043F\u0435\u0440\u0435\u043D\u0430\u0437\u043D\u0430\u0447\u0430\u044E\u0442\u0441\u044F.", pending: remove.isPending, disabled: dirty || translationDirty, error: remove.error
                    ? locationAdminError(remove.error, 'Не удалось удалить группу.')
                    : undefined, onConfirm: async () => {
                    try {
                        await remove.mutateAsync(group.id);
                        onDone();
                    }
                    catch (error) {
                        if (!(error instanceof ApiError) || error.status !== 404)
                            return;
                        try {
                            await groups.refetch({ throwOnError: true });
                        }
                        catch {
                            // The stale editor must close even if refreshing the directory also fails.
                        }
                        onDone();
                    }
                } })), _jsx(SaveBar, { dirty: creating || dirty, saving: saving, saved: update.isSuccess, canSave: !nameError && !saving && (creating || dirty), error: saveError ??
                    (currentError
                        ? locationAdminError(currentError, 'Не удалось сохранить группу.')
                        : undefined), text: creating ? 'Новая группа' : undefined, submitLabel: creating ? 'Создать' : 'Сохранить группу', onReset: creating
                    ? onDone
                    : () => {
                        setName(baseName);
                        setFieldError(null);
                        setSaveError(null);
                        update.reset();
                    } })] }));
}
